import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { BackButton } from "@/components/BackButton";
import { api, apiErr } from "@/lib/api";
import { mediaUrl } from "@/pages/Marketplace";
import { toast } from "sonner";
import { Upload, Loader2, X, ImagePlus } from "lucide-react";

export default function CreateAd() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ categories: [], ctas: [] });
  const [form, setForm] = useState({ title: "", description: "", category: "", location: "", price: "", contact: "", website: "", cta: "Learn More" });
  const [images, setImages] = useState([]); // {path, url}
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/ads/meta").then((r) => { setMeta(r.data); setForm((f) => ({ ...f, category: r.data.categories[0], cta: r.data.ctas[r.data.ctas.length - 1] })); }); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files.slice(0, 6 - images.length)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/ads/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, { path: data.path, url: data.url }]);
      }
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) { toast.error("Title and description are required."); return; }
    setSaving(true);
    try {
      const payload = { ...form, ad_type: images.length ? "image" : "text", image_paths: images.map((i) => i.path) };
      const { data } = await api.post("/ads", payload);
      toast.success("Ad published!");
      navigate(`/market/${data.ad.id}`);
    } catch (err) { toast.error(apiErr(err)); }
    finally { setSaving(false); }
  };

  const field = "w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-[#A78BFA]/50 outline-none transition-colors duration-300";

  return (
    <div className="min-h-screen bg-[#0A0A14] text-slate-200">
      <Header />
      <div className="max-w-2xl mx-auto px-6 py-10">
        <BackButton className="mb-6" />
        <h1 className="font-head font-900 text-3xl text-slate-50 mb-1">Create an ad</h1>
        <p className="text-slate-400 text-sm mb-8">Publish a free listing. Add photos to stand out.</p>

        <form onSubmit={submit} className="space-y-5" data-testid="create-ad-form">
          <input data-testid="ad-title" className={field} placeholder="Ad title" value={form.title} onChange={set("title")} maxLength={120} />
          <textarea data-testid="ad-description" className={`${field} min-h-[120px]`} placeholder="Describe what you're offering…" value={form.description} onChange={set("description")} />
          <div className="grid sm:grid-cols-2 gap-4">
            <select data-testid="ad-category" className={field} value={form.category} onChange={set("category")}>
              {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input data-testid="ad-location" className={field} placeholder="Location (city)" value={form.location} onChange={set("location")} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="ad-price" className={field} placeholder="Price (optional, e.g. $49)" value={form.price} onChange={set("price")} />
            <input data-testid="ad-contact" className={field} placeholder="Contact (phone/email, optional)" value={form.contact} onChange={set("contact")} />
          </div>
          <input data-testid="ad-website" className={field} placeholder="Website / destination URL (optional)" value={form.website} onChange={set("website")} />
          <select data-testid="ad-cta" className={field} value={form.cta} onChange={set("cta")}>
            {meta.ctas.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>

          <div>
            <label className="text-xs uppercase tracking-widest text-slate-400 block mb-2">Photos (up to 6)</label>
            <div className="flex flex-wrap gap-3">
              {images.map((img, i) => (
                <div key={i} className="relative w-24 h-24 rounded-xl overflow-hidden border border-white/10">
                  <img src={mediaUrl(img.url)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setImages((p) => p.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 bg-black/60 rounded-full p-1"><X className="w-3 h-3 text-white" /></button>
                </div>
              ))}
              {images.length < 6 && (
                <label data-testid="ad-image-upload" className="w-24 h-24 rounded-xl border border-dashed border-white/20 flex items-center justify-center cursor-pointer hover:border-[#A78BFA]/60 transition-colors duration-200">
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : <ImagePlus className="w-6 h-6 text-slate-500" />}
                  <input type="file" accept="image/*" multiple className="hidden" onChange={upload} />
                </label>
              )}
            </div>
          </div>

          <button data-testid="publish-ad-btn" type="submit" disabled={saving} className="w-full rounded-xl py-3 text-sm font-medium bg-[#A78BFA] text-slate-900 hover:-translate-y-0.5 transition-transform duration-300 inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />} Publish ad
          </button>
        </form>
      </div>
    </div>
  );
}
